import { useMatch, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Skeleton } from '@/shared/ui';
import { useAgentChat } from '@/widgets/right-side-menu/lib/agent-chat-model';
import type { AIContextMenu } from '@/widgets/right-side-menu/lib/types';
import { ChatHistoryList } from '../chat-history/ChatHistoryList';
import { ChatThreadView } from './ChatThreadView';
import { ChatWelcomeView } from './ChatWelcomeView';

type Props = {
  projectId: string;
  aiContextMenu: AIContextMenu;
  setAiContextMenu: (context: AIContextMenu) => void;
  showHistory: boolean;
  setShowHistory: (show: boolean) => void;
};

/** 事業詳細ページ用AIチャットコンテンツ。エージェントチャット/履歴の表示を切り替える */
export const ProjectDetailAIChatContents = ({
  projectId,
  aiContextMenu,
  setAiContextMenu,
  showHistory,
  setShowHistory,
}: Props) => {
  const navigate = useNavigate();

  // 事業詳細ページとメモページで AI チャットを共有しているため、URL 書き込み先を現在ページに合わせる
  const isOnMemoPage = useMatch({
    from: '/_protected/projects/$projectId/memo',
    shouldThrow: false,
  });

  const initialThreadId =
    aiContextMenu?.route === 'projectDetail' && aiContextMenu.mode === 'agent_chat'
      ? aiContextMenu.threadId
      : undefined;

  const updateUrlThreadId = (threadId: string | undefined) => {
    // 他 search params（tab / memoId）を保持するため `(prev) => ...` 形式で更新する
    if (isOnMemoPage) {
      navigate({
        to: '/projects/$projectId/memo',
        params: { projectId },
        search: (prev) => ({ ...prev, threadId }),
        replace: true,
      });
    } else {
      navigate({
        to: '/projects/$projectId',
        params: { projectId },
        search: (prev) => ({ ...prev, threadId }),
        replace: true,
      });
    }
  };

  const { messages, isLoading, isThreadFetching, sendMessage, isTokenLimitExceeded, threadId } =
    useAgentChat({
      projectId,
      initialThreadId,
      onThreadCreated: (newThreadId) => {
        setAiContextMenu({
          route: 'projectDetail',
          mode: 'agent_chat',
          threadId: newThreadId,
        });
        updateUrlThreadId(newThreadId);
      },
    });

  // initialThreadId またはルート種別（事業詳細/メモ）変化時に URL を再同期する。
  // updateUrlThreadId の参照変化では再実行したくないので deps からは外す。
  const isOnMemoPageRoute = !!isOnMemoPage;
  // biome-ignore lint/correctness/useExhaustiveDependencies: updateUrlThreadId は参照変化で再実行したくない
  useEffect(() => {
    if (initialThreadId) {
      updateUrlThreadId(initialThreadId);
    }
  }, [initialThreadId, isOnMemoPageRoute]);

  if (showHistory) {
    return (
      <ChatHistoryList
        projectId={projectId}
        setAiContextMenu={(context) => {
          setAiContextMenu(context);
          setShowHistory(false);
        }}
      />
    );
  }

  if (messages.length > 0) {
    return (
      <ChatThreadView
        key={threadId ?? 'new'}
        messages={messages}
        isLoading={isLoading}
        onSend={sendMessage}
        isTokenLimitExceeded={isTokenLimitExceeded}
      />
    );
  }

  // threadId 指定ありでスレッド本体取得中はウェルカム画面のフラッシュを防ぐため Skeleton 表示
  if (isThreadFetching) {
    return (
      <div className="flex flex-col gap-3 pt-4">
        <Skeleton className="h-16 w-3/4" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-16 w-2/3" />
      </div>
    );
  }

  return <ChatWelcomeView onSend={sendMessage} isTokenLimitExceeded={isTokenLimitExceeded} />;
};
